'use client';

/**
 * The analytics view.
 *
 * Ordered by what a child can act on, not by what is easiest to compute:
 * headline numbers → am I improving? → which keys are letting me down? →
 * where am I in the course. The benchmark comparison is deliberately framed
 * against the child's own age band rather than against a global leaderboard,
 * because comparing a seven-year-old with an adult typist is discouraging and
 * meaningless.
 */
import React from 'react';
import Link from 'next/link';
import { useKids } from '../context/KidsProvider';
import {
  overview,
  dailyHistory,
  speedSeries,
  keyHealth,
  weakKeys,
  strongKeys,
  fingerHealth,
  levelProgress,
  suggestions,
} from '../lib/analytics';
import { benchmarkFor, rankFor } from '../lib/metrics';
import { MAX_STARS } from '../lib/progress';
import { SpeedChart, PracticeChart } from './Charts';
import KeyHeatmap from './KeyHeatmap';
import { StatTile, ProgressRail, SectionHead, EmptyState, LoadingBlock, Pill } from './ui';

export default function ProgressDashboard() {
  const { profile, hydrated } = useKids();

  if (!hydrated || !profile) return <LoadingBlock height={420} />;

  const stats = overview(profile);

  if (stats.totalSessions === 0) {
    return (
      <EmptyState
        emoji="📊"
        title="No results yet"
        body="Finish your first lesson and this page fills up with your speed, your accuracy, and a map of every key you have typed."
        href="/kids/journey"
        cta="Start Level 1"
      />
    );
  }

  const rank = rankFor(profile.xp);
  const benchmark = benchmarkFor(profile.ageBand);
  const weak = weakKeys(profile, 6);
  const strong = strongKeys(profile, 6);
  const fingers = fingerHealth(profile);
  const levels = levelProgress(profile);
  const tips = suggestions(profile);

  return (
    <div className="grid gap-8">
      <SectionHead
        as="h1"
        eyebrow="Your numbers"
        title="My typing progress"
        sub={`${stats.totalSessions} lessons finished over ${stats.practiceDays} day${
          stats.practiceDays === 1 ? '' : 's'
        }, ${stats.totalMinutes} minutes of practice in total.`}
      />

      {/* ── headline ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 k-stagger">
        <StatTile
          label="Best speed"
          value={`${stats.bestWpm}`}
          sub="words per minute"
          emoji="⚡"
          color="--k-sky"
        />
        <StatTile
          label="Right now"
          value={`${stats.recentWpm}`}
          sub="avg of last 10"
          emoji="📈"
          color="--k-grape"
        />
        <StatTile
          label="Accuracy"
          value={`${stats.recentAccuracy}%`}
          sub="last 10 lessons"
          emoji="🎯"
          color="--k-mint"
        />
        <StatTile
          label="Improvement"
          value={stats.trendPct > 0 ? `+${stats.trendPct}%` : `${stats.trendPct}%`}
          sub="since you started"
          emoji="🚀"
          color="--k-mango"
        />
      </div>

      {/* ── age benchmark ────────────────────────────────────────────────── */}
      <section className="k-card p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <h2 className="text-base">How you compare</h2>
          <span className="text-xs text-muted-foreground">
            {benchmark.label} · teaching guideline
          </span>
        </div>
        <div className="relative pt-6 pb-2">
          <ProgressRail
            value={Math.min(stats.recentWpm, benchmark.strong * 1.4)}
            max={benchmark.strong * 1.4}
          />
          {/* Reference markers, labelled — never colour alone. */}
          {[
            { value: benchmark.typical, label: 'Typical' },
            { value: benchmark.strong, label: 'Strong' },
          ].map((marker) => (
            <span
              key={marker.label}
              className="absolute text-[0.62rem] font-extrabold text-muted-foreground"
              style={{
                left: `${(marker.value / (benchmark.strong * 1.4)) * 100}%`,
                top: 0,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
              }}
            >
              {marker.label} {marker.value}
              <span
                className="block mx-auto mt-0.5"
                style={{ width: 2, height: 8, background: 'var(--border-strong)' }}
                aria-hidden="true"
              />
            </span>
          ))}
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          You are typing at <strong className="text-foreground">{stats.recentWpm} wpm</strong>. For{' '}
          {benchmark.label.toLowerCase()}, around {benchmark.typical} wpm is typical and{' '}
          {benchmark.strong} wpm is strong. These are teaching guidelines, not a test score.
        </p>
      </section>

      {/* ── charts ───────────────────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-2">
        <SpeedChart data={speedSeries(profile)} />
        <PracticeChart data={dailyHistory(profile, 21)} />
      </div>

      {/* ── keys ─────────────────────────────────────────────────────────── */}
      <KeyHeatmap data={keyHealth(profile)} />

      <div className="grid gap-4 md:grid-cols-2">
        <KeyList
          title="Keys to practise"
          emoji="🔧"
          empty="Nothing is dragging you down right now — every key you have typed is above 92%."
          keys={weak}
          color="--chart-low"
        />
        <KeyList
          title="Your strongest keys"
          emoji="💪"
          empty="Type a bit more and your best keys will show up here."
          keys={strong}
          color="--chart-high"
        />
      </div>

      {/* ── fingers ──────────────────────────────────────────────────────── */}
      {fingers.length > 0 && (
        <section className="k-card p-5">
          <h2 className="text-base mb-1">Accuracy by finger</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Ring and pinky fingers are almost always the weakest. That is normal — they just need
            more repetitions.
          </p>
          <ul className="grid gap-2.5">
            {fingers
              .slice()
              .sort((a, b) => a.accuracy - b.accuracy)
              .map((finger) => (
                <li key={finger.id} className="flex items-center gap-3">
                  <span
                    className="inline-block rounded-full shrink-0"
                    style={{ width: 12, height: 12, background: `var(${finger.cssVar})` }}
                    aria-hidden="true"
                  />
                  <span className="w-32 shrink-0 text-sm font-bold capitalize">{finger.label}</span>
                  <span className="flex-1">
                    <ProgressRail value={finger.accuracy} max={100} color={finger.cssVar} />
                  </span>
                  <span className="w-20 text-right text-sm font-extrabold tabular-nums">
                    {finger.accuracy}%
                  </span>
                </li>
              ))}
          </ul>
        </section>
      )}

      {/* ── course map ───────────────────────────────────────────────────── */}
      <section className="k-card p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h2 className="text-base">Journey progress</h2>
          <Pill color="--k-mango">
            ⭐ {stats.stars} / {MAX_STARS} stars
          </Pill>
        </div>
        <ul className="grid gap-2">
          {levels.map((level) => (
            <li key={level.id} className="flex items-center gap-3">
              <span className="w-8 shrink-0 text-center" aria-hidden="true">
                {level.emoji}
              </span>
              <span className="w-36 shrink-0 text-sm font-bold truncate">{level.title}</span>
              <span className="flex-1">
                <ProgressRail value={level.pct} max={100} color={level.color} />
              </span>
              <span className="w-16 text-right text-xs font-extrabold tabular-nums text-muted-foreground">
                {level.done}/{level.total}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground mt-4">
          Rank {rank.rank.level}: {rank.rank.emoji} {rank.rank.name}
          {rank.next && ` — ${rank.next.from - profile.xp} XP until ${rank.next.name}`}
        </p>
      </section>

      {/* ── coaching ─────────────────────────────────────────────────────── */}
      {tips.length > 0 && (
        <section>
          <h2 className="k-eyebrow mb-3">What to work on next</h2>
          <div className="grid gap-3 md:grid-cols-2">
            {tips.map((tip) => (
              <article key={tip.id} className="k-card p-4 flex gap-3">
                <span style={{ fontSize: 24 }} aria-hidden="true">
                  {tip.emoji}
                </span>
                <div>
                  <h3 className="text-sm font-extrabold">{tip.title}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{tip.body}</p>
                  {tip.href && tip.cta && (
                    <Link
                      href={tip.href}
                      className="inline-block mt-2 text-sm font-extrabold"
                      style={{ color: 'var(--accent)' }}
                    >
                      {tip.cta} →
                    </Link>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function KeyList({
  title,
  emoji,
  empty,
  keys,
  color,
}: {
  title: string;
  emoji: string;
  empty: string;
  keys: { char: string; label: string; accuracy: number; attempts: number; fingerLabel: string }[];
  color: string;
}) {
  return (
    <section className="k-card p-5">
      <h2 className="text-base mb-3">
        <span aria-hidden="true">{emoji}</span> {title}
      </h2>
      {keys.length === 0 ? (
        <p className="text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="grid gap-2">
          {keys.map((key) => (
            <li key={key.char} className="flex items-center gap-3">
              <span
                className="grid place-items-center rounded-lg shrink-0 font-mono font-extrabold"
                style={{
                  width: 34,
                  height: 34,
                  background: `color-mix(in srgb, var(${color}) 16%, transparent)`,
                }}
              >
                {key.label}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-bold tabular-nums">
                  {key.accuracy}% accurate
                </span>
                <span className="block text-xs text-muted-foreground truncate">
                  {key.attempts} presses · {key.fingerLabel}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
